import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { ReactFlipBook } from "@vuvandinh203/react-flipbook";
import { jsPDF } from "jspdf";
import "./PromoFlipbook.css";

const PAGE_W = 457;
const PAGE_H = 400;
const BANNER_API = "https://api.ebusso.com/ws-get-banner";
const SHOP_PROMOS = "https://shop.ebusso.com/promotions";
const PAGE_FLIP_CDN =
  "https://cdn.jsdelivr.net/npm/page-flip@2.0.7/dist/js/page-flip.browser.js";

const FALLBACK_BANNERS = [
  {
    image: "https://demo.ebusso.com/assets/banners/promo-1.jpg",
    promoId: "demo-1",
    link: `${SHOP_PROMOS}?promo_id=demo-1`,
  },
  {
    image: "https://demo.ebusso.com/assets/banners/promo-2.jpg",
    promoId: "demo-2",
    link: `${SHOP_PROMOS}?promo_id=demo-2`,
  },
  {
    image: "https://demo.ebusso.com/assets/banners/promo-3.jpg",
    promoId: "demo-3",
    link: `${SHOP_PROMOS}?promo_id=demo-3`,
  },
  {
    image: "https://demo.ebusso.com/assets/banners/promo-4.jpg",
    promoId: "demo-4",
    link: `${SHOP_PROMOS}?promo_id=demo-4`,
  },
  {
    image: "https://demo.ebusso.com/assets/banners/promo-5.jpg",
    promoId: "demo-5",
    link: `${SHOP_PROMOS}?promo_id=demo-5`,
  },
];

function toAbsoluteUrl(value) {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return `https://${trimmed.replace(/^\/+/, "")}`;
}

function resolvePromoLink(item) {
  const direct = item?.link || item?.url || item?.promo_link;
  if (direct) return toAbsoluteUrl(String(direct));
  const promoId = item?.promo_id || item?.promoId || "";
  if (promoId) {
    return `${SHOP_PROMOS}?promo_id=${encodeURIComponent(String(promoId))}`;
  }
  return SHOP_PROMOS;
}

function normalizeBanner(item) {
  const image = toAbsoluteUrl(item?.image || item?.banner || item?.img || "");
  const promoId = String(item?.promo_id || item?.promoId || "");
  return {
    image,
    promoId,
    link: resolvePromoLink(item),
  };
}

function normalizeSlider(payload) {
  const list = Array.isArray(payload?.slider)
    ? payload.slider
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
        ? payload
        : [];
  return list
    .map(normalizeBanner)
    .filter((item) => Boolean(item.image));
}

async function fetchBanners(token) {
  if (!token) return FALLBACK_BANNERS;
  const url = `${BANNER_API}?token=${encodeURIComponent(token)}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    throw new Error("banner_request_failed");
  }
  const data = await response.json();
  if (!data || data.success === false) {
    throw new Error("banner_response_invalid");
  }
  const banners = normalizeSlider(data);
  if (!banners.length) {
    throw new Error("banner_empty");
  }
  return banners;
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function downloadBlob(blob, filename) {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1500);
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("read_failed"));
    reader.readAsDataURL(blob);
  });
}

function loadHtmlImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image_load_failed"));
    img.src = src;
  });
}

function weservProxy(url) {
  const cleaned = String(url || "").replace(/^https?:\/\//i, "");
  return `https://images.weserv.nl/?url=${encodeURIComponent(cleaned)}&output=jpg`;
}

async function loadImageDataUrl(url) {
  const attempts = [url, weservProxy(url)];
  let lastError = null;
  for (const attempt of attempts) {
    try {
      const response = await fetch(attempt, { mode: "cors" });
      if (!response.ok) {
        throw new Error("fetch_failed");
      }
      const blob = await response.blob();
      const dataUrl = await blobToDataUrl(blob);
      await loadHtmlImage(dataUrl);
      return dataUrl;
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error("image_unavailable");
}

function splitImageToDataUrls(dataUrl) {
  return loadHtmlImage(dataUrl).then((img) => {
    const halfW = Math.max(1, Math.floor(img.naturalWidth / 2));
    const height = Math.max(1, img.naturalHeight);
    const canvas = document.createElement("canvas");
    canvas.width = halfW;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, halfW, height, 0, 0, halfW, height);
    const left = canvas.toDataURL("image/jpeg", 0.92);
    ctx.clearRect(0, 0, halfW, height);
    ctx.drawImage(img, halfW, 0, halfW, height, 0, 0, halfW, height);
    const right = canvas.toDataURL("image/jpeg", 0.92);
    return { left, right };
  });
}

function addCoverPdfPage(doc, title, subtitle) {
  doc.setFillColor(26, 20, 16);
  doc.rect(0, 0, PAGE_W, PAGE_H, "F");
  doc.setTextColor(246, 231, 212);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(28);
  doc.text(title, PAGE_W / 2, PAGE_H / 2 - 8, { align: "center" });
  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(210, 190, 170);
    doc.text(subtitle, PAGE_W / 2, PAGE_H / 2 + 18, { align: "center" });
  }
}

function addLinkedImagePage(doc, imageData, link) {
  doc.addImage(imageData, "JPEG", 0, 0, PAGE_W, PAGE_H, undefined, "FAST");
  if (link) {
    doc.link(0, 0, PAGE_W, PAGE_H, { url: link });
  }
}

function addTextFallbackPage(doc, label, link) {
  doc.setFillColor(24, 28, 36);
  doc.rect(0, 0, PAGE_W, PAGE_H, "F");
  doc.setTextColor(242, 239, 232);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(label, PAGE_W / 2, PAGE_H / 2 - 10, { align: "center" });
  if (link) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(212, 163, 115);
    doc.text(link, PAGE_W / 2, PAGE_H / 2 + 16, {
      align: "center",
      maxWidth: PAGE_W - 48,
    });
    doc.link(24, PAGE_H / 2 - 40, PAGE_W - 48, 80, { url: link });
  }
}

async function buildPdf(banners) {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: [PAGE_W, PAGE_H],
    compress: true,
  });

  addCoverPdfPage(doc, "Promotions", "Ebusso promo flipbook");

  for (let i = 0; i < banners.length; i += 1) {
    const banner = banners[i];
    try {
      const dataUrl = await loadImageDataUrl(banner.image);
      const halves = await splitImageToDataUrls(dataUrl);
      doc.addPage([PAGE_W, PAGE_H], "portrait");
      addLinkedImagePage(doc, halves.left, banner.link);
      doc.addPage([PAGE_W, PAGE_H], "portrait");
      addLinkedImagePage(doc, halves.right, banner.link);
    } catch {
      doc.addPage([PAGE_W, PAGE_H], "portrait");
      addTextFallbackPage(doc, `Promo ${i + 1} — Left`, banner.link);
      doc.addPage([PAGE_W, PAGE_H], "portrait");
      addTextFallbackPage(doc, `Promo ${i + 1} — Right`, banner.link);
    }
  }

  doc.addPage([PAGE_W, PAGE_H], "portrait");
  addCoverPdfPage(doc, "Thank You", "shop.ebusso.com/promotions");

  return doc.output("blob");
}

function buildStandaloneHtml(banners) {
  const bannerJson = JSON.stringify(
    banners.map((banner) => ({
      image: banner.image,
      link: banner.link,
    })),
  );

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
  <title>Ebusso Flipbook</title>
  <style>
    :root {
      color-scheme: dark;
      --page-w: ${PAGE_W}px;
      --page-h: ${PAGE_H}px;
      --book-w: calc(var(--page-w) * 2);
    }
    * { box-sizing: border-box; -webkit-tap-highlight-color: transparent; }
    html, body { margin: 0; height: 100%; }
    body {
      min-height: 100vh;
      min-height: 100dvh;
      background: linear-gradient(180deg, #141820, #0b0d11);
      color: #f2efe8;
      font-family: Georgia, "Times New Roman", serif;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px 8px;
      overflow: hidden;
    }
    .shell {
      width: 100%;
      max-width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
    }
    .book-frame {
      position: relative;
      width: var(--book-w);
      height: var(--page-h);
      max-width: 100%;
      margin: 0 auto;
      overflow: visible;
      flex-shrink: 1;
    }
    .nav {
      position: relative;
      z-index: 30;
      flex-shrink: 0;
      width: 44px;
      height: 44px;
      border: 1px solid rgba(255,255,255,0.18);
      border-radius: 999px;
      background: rgba(0,0,0,0.65);
      color: #fff;
      font-size: 26px;
      line-height: 1;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      touch-action: manipulation;
    }
    .nav:hover, .nav:active { background: rgba(0,0,0,0.85); }
    #book,
    .stf__parent,
    .stf__wrapper,
    .stf__block {
      width: 100% !important;
      height: var(--page-h) !important;
      max-width: 100%;
      margin: 0 auto !important;
      overflow: hidden !important;
    }
    .stf__corner,
    .stf__corner--active {
      display: none !important;
      pointer-events: none !important;
    }
    .page {
      width: var(--page-w);
      height: var(--page-h);
      background: #1b1f28;
      position: relative;
      overflow: hidden !important;
    }
    .page[data-density="hard"] {
      background: linear-gradient(180deg, #2a2118, #100c0a);
    }
    .cover {
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: clamp(12px, 4vw, 24px);
      color: #f6e7d4;
    }
    .kicker {
      margin: 0 0 10px;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      font-size: clamp(0.6rem, 2.2vw, 0.72rem);
      font-family: system-ui, sans-serif;
      color: rgba(246,231,212,0.7);
    }
    .cover h1 { margin: 0; font-size: clamp(1.25rem, 5vw, 2rem); }
    .sub {
      margin: 12px 0 0;
      font-size: clamp(0.75rem, 2.5vw, 1rem);
      font-family: system-ui, sans-serif;
      color: rgba(246,231,212,0.72);
    }
    .media {
      position: absolute;
      inset: 0;
      background-repeat: no-repeat;
      background-size: 200% 100%;
    }
    .media.left { background-position: left center; }
    .media.right { background-position: right center; }
    .media.full {
      background-size: 100% 100%;
      background-position: center center;
    }
    .hotspot {
      position: absolute;
      inset: 0;
      z-index: 2;
      text-indent: -9999px;
      overflow: hidden;
    }
    @media (max-width: 720px) {
      body { padding: 12px 6px 20px; }
      .shell {
        flex-direction: column;
        gap: 14px;
      }
      .nav-row {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 20px;
        order: 2;
      }
      .book-frame { order: 1; }
      .nav { width: 48px; height: 48px; font-size: 28px; }
    }
  </style>
</head>
<body>
  <div class="shell">
    <button type="button" class="nav prev" id="prevBtn" aria-label="Previous">‹</button>
    <div class="book-frame" id="bookFrame">
      <div id="book"></div>
    </div>
    <button type="button" class="nav next" id="nextBtn" aria-label="Next">›</button>
  </div>
  <script src="${PAGE_FLIP_CDN}"></script>
  <script>
    (function () {
      var BASE_W = ${PAGE_W};
      var BASE_H = ${PAGE_H};
      var FULL_W = BASE_W * 2;
      var MOBILE_BP = 720;
      var banners = ${bannerJson};
      var bookEl = document.getElementById("book");
      var frameEl = document.getElementById("bookFrame");
      var shellEl = document.querySelector(".shell");
      var prevBtn = document.getElementById("prevBtn");
      var nextBtn = document.getElementById("nextBtn");
      var pageFlip = null;
      var lastKey = "";
      var wasMobile = null;

      function escapeHtml(value) {
        return String(value || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");
      }

      function isMobile() {
        return window.innerWidth <= MOBILE_BP;
      }

      function coverPage(title, sub) {
        return (
          '<div class="page" data-density="hard">' +
            '<div class="cover">' +
              '<p class="kicker">Ebusso</p>' +
              "<h1>" + title + "</h1>" +
              '<p class="sub">' + sub + "</p>" +
            "</div>" +
          "</div>"
        );
      }

      function buildPages(mobile) {
        var html = [coverPage("Promotions", "Open-book magazine preview")];
        banners.forEach(function (banner, index) {
          var safeLink = escapeHtml(banner.link);
          var safeImage = escapeHtml(banner.image);
          var n = index + 1;
          if (mobile) {
            html.push(
              '<div class="page" data-density="soft">' +
                '<div class="media full" style="background-image:url(\\'' + safeImage + '\\')"></div>' +
                '<a class="hotspot" href="' + safeLink + '" target="_blank" rel="noopener noreferrer">Promo ' + n + "</a>" +
              "</div>"
            );
          } else {
            html.push(
              '<div class="page" data-density="soft">' +
                '<div class="media left" style="background-image:url(\\'' + safeImage + '\\')"></div>' +
                '<a class="hotspot" href="' + safeLink + '" target="_blank" rel="noopener noreferrer">Promo ' + n + " left</a>" +
              "</div>"
            );
            html.push(
              '<div class="page" data-density="soft">' +
                '<div class="media right" style="background-image:url(\\'' + safeImage + '\\')"></div>' +
                '<a class="hotspot" href="' + safeLink + '" target="_blank" rel="noopener noreferrer">Promo ' + n + " right</a>" +
              "</div>"
            );
          }
        });
        html.push(coverPage("Thank You", "Visit shop.ebusso.com/promotions"));
        return html.join("\\n");
      }

      function mapPageIndex(fromMobile, toMobile, index) {
        if (fromMobile === toMobile) return index;
        if (index <= 0) return 0;
        var bannerCount = banners.length;
        if (fromMobile && !toMobile) {
          if (index >= bannerCount + 1) return bannerCount * 2 + 1;
          return (index - 1) * 2 + 1;
        }
        if (!fromMobile && toMobile) {
          if (index >= bannerCount * 2 + 1) return bannerCount + 1;
          return Math.floor((index - 1) / 2) + 1;
        }
        return 0;
      }

      function layoutNav() {
        var mobile = isMobile();
        var existing = document.querySelector(".nav-row");
        if (mobile) {
          if (!existing) {
            var row = document.createElement("div");
            row.className = "nav-row";
            row.appendChild(prevBtn);
            row.appendChild(nextBtn);
            shellEl.appendChild(row);
          }
        } else if (existing) {
          shellEl.insertBefore(prevBtn, frameEl);
          shellEl.appendChild(nextBtn);
          existing.remove();
        }
      }

      function calcSize() {
        var mobile = isMobile();
        var padX = mobile ? 16 : 120;
        var padY = mobile ? 110 : 48;
        var availW = Math.max(200, window.innerWidth - padX);
        var availH = Math.max(160, window.innerHeight - padY);
        var pageW;
        var pageH;
        if (mobile) {
          pageW = Math.min(FULL_W, availW);
          pageH = pageW * (BASE_H / FULL_W);
          if (pageH > availH) {
            pageH = availH;
            pageW = pageH * (FULL_W / BASE_H);
          }
        } else {
          var bookW = Math.min(FULL_W, availW);
          pageW = bookW / 2;
          pageH = pageW * (BASE_H / BASE_W);
          if (pageH > availH) {
            pageH = availH;
            pageW = pageH * (BASE_W / BASE_H);
          }
        }
        return {
          pageW: Math.floor(pageW),
          pageH: Math.floor(pageH),
          mobile: mobile
        };
      }

      function applyCssVars(size) {
        var root = document.documentElement;
        root.style.setProperty("--page-w", size.pageW + "px");
        root.style.setProperty("--page-h", size.pageH + "px");
        root.style.setProperty("--book-w", size.pageW * (size.mobile ? 1 : 2) + "px");
      }

      function destroyFlip() {
        if (!pageFlip) return;
        try { pageFlip.destroy(); } catch (e) {}
        pageFlip = null;
      }

      function initFlip() {
        var size = calcSize();
        var key = size.pageW + "x" + size.pageH + (size.mobile ? "m" : "d");
        if (key === lastKey && pageFlip) return;
        var prevIndex = pageFlip ? pageFlip.getCurrentPageIndex() : 0;
        var nextIndex = mapPageIndex(wasMobile, size.mobile, prevIndex);
        lastKey = key;
        wasMobile = size.mobile;
        destroyFlip();
        bookEl.innerHTML = buildPages(size.mobile);
        applyCssVars(size);
        layoutNav();
        pageFlip = new St.PageFlip(bookEl, {
          width: size.pageW,
          height: size.pageH,
          size: "fixed",
          autoSize: false,
          showCover: true,
          usePortrait: size.mobile,
          drawShadow: !size.mobile,
          maxShadowOpacity: 0.55,
          showPageCorners: false,
          disableFlipByClick: false,
          clickEventForward: true,
          useMouseEvents: false,
          mobileScrollSupport: false,
          swipeDistance: 99999
        });
        pageFlip.loadFromHTML(document.querySelectorAll("#book .page"));
        if (nextIndex > 0) {
          try { pageFlip.turnToPage(nextIndex); } catch (e) {}
        }
      }

      function goPrev() {
        if (!pageFlip) return;
        try {
          pageFlip.flipPrev();
        } catch (e) {
          pageFlip.turnToPrevPage();
        }
      }

      function goNext() {
        if (!pageFlip) return;
        try {
          pageFlip.flipNext();
        } catch (e) {
          pageFlip.turnToNextPage();
        }
      }

      prevBtn.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        goPrev();
      });
      nextBtn.addEventListener("click", function (event) {
        event.preventDefault();
        event.stopPropagation();
        goNext();
      });
      document.addEventListener("keydown", function (event) {
        if (!pageFlip) return;
        if (event.key === "ArrowLeft") goPrev();
        if (event.key === "ArrowRight") goNext();
      });

      var resizeTimer = null;
      window.addEventListener("resize", function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(initFlip, 120);
      });
      window.addEventListener("orientationchange", function () {
        setTimeout(initFlip, 200);
      });

      initFlip();
    })();
  </script>
</body>
</html>`;
}

const CoverPage = forwardRef(function CoverPage({ title, subtitle, kicker }, ref) {
  return (
    <div className="pf-page pf-page-cover" ref={ref} data-density="hard">
      <div className="pf-cover-inner">
        <p className="pf-cover-kicker">{kicker || "Ebusso"}</p>
        <h2 className="pf-cover-title">{title}</h2>
        {subtitle ? <p className="pf-cover-sub">{subtitle}</p> : null}
      </div>
    </div>
  );
});

const BannerHalfPage = forwardRef(function BannerHalfPage(
  { image, link, side },
  ref,
) {
  return (
    <div className="pf-page pf-page-banner pf-page-soft" ref={ref} data-density="soft">
      <div
        className={`pf-media ${side === "right" ? "pf-media-right" : "pf-media-left"}`}
        style={{ backgroundImage: `url("${image}")` }}
      />
      <a
        className="pf-page-link"
        href={link}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open promotion
      </a>
    </div>
  );
});

export default function PromoFlipbook({ token }) {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  const resolvedToken = useMemo(() => {
    if (token) return String(token);
    if (typeof process !== "undefined" && process.env?.REACT_APP_EBUSSO_TOKEN) {
      return String(process.env.REACT_APP_EBUSSO_TOKEN);
    }
    return "";
  }, [token]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchBanners(resolvedToken)
      .then((items) => {
        if (active) setBanners(items);
      })
      .catch(() => {
        if (active) setBanners(FALLBACK_BANNERS);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [resolvedToken]);

  const ready = !loading && banners.length > 0;

  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/slider_settings");
  }, [navigate]);

  const handleDownloadHtml = useCallback(() => {
    if (!ready) return;
    const html = buildStandaloneHtml(banners);
    downloadBlob(new Blob([html], { type: "text/html;charset=utf-8" }), "ebusso-promotions-flipbook.html");
  }, [banners, ready]);

  const pages = useMemo(() => {
    const nodes = [
      <CoverPage
        key="cover-front"
        title="Promotions"
        subtitle="Turn the page to explore offers"
        kicker="Ebusso"
      />,
    ];
    banners.forEach((banner, index) => {
      nodes.push(
        <BannerHalfPage
          key={`banner-${index}-left`}
          image={banner.image}
          link={banner.link}
          side="left"
        />,
      );
      nodes.push(
        <BannerHalfPage
          key={`banner-${index}-right`}
          image={banner.image}
          link={banner.link}
          side="right"
        />,
      );
    });
    nodes.push(
      <CoverPage
        key="cover-back"
        title="Thank You"
        subtitle="shop.ebusso.com/promotions"
        kicker="Ebusso"
      />,
    );
    return nodes;
  }, [banners]);

  return (
    <div className="pf-shell">
      <header className="pf-header">
        <button type="button" className="pf-btn pf-btn-back" onClick={handleBack}>
          ← Back
        </button>
        <button
          type="button"
          className="pf-btn pf-btn-primary"
          onClick={handleDownloadHtml}
          disabled={!ready}
        >
          Download HTML
        </button>
      </header>

      <div className="pf-stage">
        {loading ? (
          <div className="pf-loading">Loading promotions…</div>
        ) : (
          <div className="pf-book-wrap">
            <ReactFlipBook
              width={PAGE_W}
              height={PAGE_H}
              size="fixed"
              autoSize={false}
              usePortrait={false}
              showCover={true}
              drawShadow={true}
              maxShadowOpacity={0.55}
              showPageCorners={true}
              disableFlipByClick={true}
              clickEventForward={true}
              useMouseEvents={true}
              mobileScrollSupport={true}
              showNavigationButtons={true}
              enableKeyboardNav={true}
              renderOnlyPageLengthChange={true}
              className="pf-flipbook"
              renderNavigationButton={(type, onClick) => (
                <button
                  type="button"
                  className={`pf-nav ${type === "prev" ? "pf-nav-prev" : "pf-nav-next"}`}
                  onClick={onClick}
                  aria-label={type === "prev" ? "Previous" : "Next"}
                >
                  {type === "prev" ? "‹" : "›"}
                </button>
              )}
            >
              {pages}
            </ReactFlipBook>
          </div>
        )}
      </div>
    </div>
  );
}
